import React, { useMemo } from 'react';
import { Trophy, Star, Flame, Sparkles } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayAvatar } from '../../../mascot';
import { AchievementsSection } from '../../../components/progress/AchievementsSection';
import { getProgressToNextLevel } from '../../../config/constants';
import { getLevelTitle } from '../../../utils/motivationalMessages';
import { MODULES } from '../../../content';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
interface PrestatiePageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
}
export function PrestatiePage({
  linkedLearners,
  selectedChild,
  childData,
  getLearnerModuleMastery,
  setViewingLearner
}: PrestatiePageProps) {
  const {
    totalQuestionsAnswered,
    masteredCount
  } = useMemo(() => {
    if (!selectedChild || !childData) {
      return {
        totalQuestionsAnswered: 0,
        masteredCount: 0
      };
    }
    // Count total questions answered from questionStates
    const totalQs = Object.keys(childData.questionStates || {}).length;
    // Count mastered modules
    let mastered = 0;
    MODULES.forEach(m => {
      const mastery = getLearnerModuleMastery(selectedChild.id, m.id);
      if (mastery >= 95) mastered++;
    });
    return {
      totalQuestionsAnswered: totalQs,
      masteredCount: mastered
    };
  }, [selectedChild, childData, getLearnerModuleMastery]);
  if (!selectedChild || !childData) {
    return <PageLayout title="Prestaties" description="Bekijk behaalde doelen" data-id="element-4278">
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-4" data-id="element-4279">
          <DjayAvatar size="lg" emotion="thinking" data-id="element-4280" />
          <p className="text-slate-500 font-medium" data-id="element-4281">
            Selecteer een kind om prestaties te bekijken.
          </p>
        </div>
      </PageLayout>;
  }
  const xp = childData.stats.xp || 0;
  const level = childData.stats.level || 1;
  const progress = getProgressToNextLevel(xp);
  const currentStreak = childData.dailyStreak?.currentWeekDays || 0;
  return <PageLayout title="Prestaties" description={`Bekijk de prestaties en beloningen van ${selectedChild.displayName}`} data-id="element-4282">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-4283">
        {/* Child Selector */}
        {linkedLearners.length > 1 && <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-4284">
            {linkedLearners.map(learner => <button key={learner.id} onClick={() => setViewingLearner(learner.id)} className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${selectedChild.id === learner.id ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`} data-id="element-4285">
                {learner.displayName}
              </button>)}
          </div>}

        {/* Level Progress Card */}
        <Card className="p-6 bg-white border-slate-200 shadow-sm overflow-hidden relative" data-id="element-4286">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-50 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none opacity-60" data-id="element-4287" />

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8" data-id="element-4288">
            <div className="w-32 h-32 bg-emerald-50 rounded-3xl flex items-center justify-center border-2 border-emerald-100 shadow-inner flex-shrink-0 relative" data-id="element-4289">
              <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-amber-300 to-orange-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-20" data-id="element-4290">
                <span className="text-white font-black text-sm" data-id="element-4291">{level}</span>
              </div>
              <DjayAvatar size="lg" emotion="proud" data-id="element-4292" />
            </div>

            <div className="flex-1 w-full space-y-4" data-id="element-4293">
              <div data-id="element-4294">
                <div className="flex items-center justify-between mb-1" data-id="element-4295">
                  <h3 className="text-xl font-black text-slate-800" data-id="element-4296">
                    {getLevelTitle(level)}
                  </h3>
                  <span className="text-sm font-bold text-emerald-600" data-id="element-4297">
                    Level {level}
                  </span>
                </div>
                <p className="text-sm text-slate-500 font-medium" data-id="element-4298">
                  Nog {progress.needed - progress.current} XP tot het volgende
                  level
                </p>
              </div>

              <div className="space-y-2" data-id="element-4299">
                <div className="h-4 bg-slate-100 rounded-full overflow-hidden shadow-inner" data-id="element-4300">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-1000 ease-out relative" style={{
                  width: `${Math.max(5, progress.percent)}%`
                }} data-id="element-4301">
                    <div className="absolute inset-0 bg-white/20 w-full h-full" style={{
                    backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                    backgroundSize: '1rem 1rem'
                  }} data-id="element-4302" />
                  </div>
                </div>
                <div className="flex justify-between text-xs font-bold text-slate-400" data-id="element-4303">
                  <span data-id="element-4304">{xp} XP</span>
                  <span data-id="element-4305">{xp + (progress.needed - progress.current)} XP</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4306">
          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4307">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0" data-id="element-4308">
              <Flame className="w-5 h-5 text-orange-500" data-id="element-4309" />
            </div>
            <div data-id="element-4310">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4311">
                Reeks
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-4312">
                {currentStreak} dagen
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4313">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-4314">
              <Star className="w-5 h-5 text-blue-500" data-id="element-4315" />
            </div>
            <div data-id="element-4316">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4317">
                Vragen
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-4318">
                {totalQuestionsAnswered}
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4319">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-4320">
              <Trophy className="w-5 h-5 text-purple-500" data-id="element-4321" />
            </div>
            <div data-id="element-4322">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4323">
                Meester
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-4324">
                {masteredCount} vakken
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-4325">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-4326">
              <Sparkles className="w-5 h-5 text-amber-500" data-id="element-4327" />
            </div>
            <div data-id="element-4328">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4329">
                Totaal XP
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-4330">{xp}</div>
            </div>
          </Card>
        </div>

        {/* Achievements Section */}
        <div className="pt-4" data-id="element-4331">
          <AchievementsSection state={childData} totalQuestionsAnswered={totalQuestionsAnswered} masteredCount={masteredCount} currentStreak={currentStreak} data-id="element-4332" />
        </div>
      </div>
    </PageLayout>;
}